import { Navigate, RouteObject, createBrowserRouter } from 'react-router-dom';
import WrapperRouteComponent from './config';
import Middleware from '@/middleware';
import { PAGE_TITLES } from '@/constants/titles';
import MainLayout from '@/layouts/main.layout';
import AuthLayout from '@/layouts/auth.layout';
import KYCLayout from '@/layouts/kyc.layout';
import { AuthWrapper } from '@/components/HOC/AuthWrapper';
import { lazyRetry } from '@/utils/lazy-retry';
import EntityWorkspaceGate from '@/components/HOC/EntityWorkspaceGate';
import EntityOnboardingGate from '@/components/HOC/EntityOnboardingGate';
import EntitySelectionScreen from '@/components/Entity/entity-selection-screen';
import DrawerProvider from '@/components/drawer.provider';
import { WalletCardModalProvider } from '@/modules/wallets/detail/modals';
import SendMoneyModalProvider from '@/modules/wallets/send-money/modals';

// Lazy load các components

// Main menu
const NotificationsPage = lazyRetry(
  () => import(/* webpackChunkName: "notifications"*/ '@/pages/Notifications'),
);

const WalletsPage = lazyRetry(() => import('@/modules/wallets'));
const WalletDetailPage = lazyRetry(() => import('@/modules/wallets/detail'));
const WalletTopUpPage = lazyRetry(() => import('@/modules/wallets/top-up'));
const WalletConvertPage = lazyRetry(() => import('@/modules/wallets/convert'));
const WalletSendMoneyPage = lazyRetry(() => import('@/modules/wallets/send-money'));
const FxPage = lazyRetry(() => import('@/modules/fx'));
const WalletPayoutPage = lazyRetry(() => import('@/modules/wallets/payout'));
const ReceivingAccountsPage = lazyRetry(() => import('@/modules/receiving-accounts'));
const CardsPage = lazyRetry(() => import('@/modules/cards'));
const TransactionsPage = lazyRetry(() => import('@/modules/transactions'));
const ComingSoonPage = lazyRetry(() => import('@/pages/ComingSoon'));

const ProfilePage = lazyRetry(
  () => import(/* webpackChunkName: "settings"*/ '@/pages/Settings/profile'),
);
const EntityPermissionsPage = lazyRetry(
  () => import(/* webpackChunkName: "settings-permissions"*/ '@/modules/permissions'),
);
const EntityPermissionUserDetailPage = lazyRetry(
  () => import(/* webpackChunkName: "settings-permissions-user"*/ '@/modules/permissions/user-detail'),
);
const DepartmentsPage = lazyRetry(() => import('@/modules/departments'));
const DepartmentDetailPage = lazyRetry(() => import('@/modules/departments/detail'));
const NotificationSettingsPage = lazyRetry(() => import('@/modules/notification-settings'));

const InviteAcceptPage = lazyRetry(
  () => import(/* webpackChunkName: "invite"*/ '@/pages/InviteAccept'),
);
const InviteIdPage = lazyRetry(
  () => import(/* webpackChunkName: "invite-id"*/ '@/pages/InviteAccept/id-page'),
);

// Auth
const AuthPage = lazyRetry(() => import(/* webpackChunkName: "login"*/ '@/pages/Auth'));

// KYC Pages
// KYCSubmitPage renders KycWizard; the id-document step embeds the Sumsub WebSDK inline.
const KYCSubmitPage = lazyRetry(() => import('@/pages/KYC/routes/submit'));
const KYCPendingPage = lazyRetry(() => import('@/pages/KYC/routes/pending'));
const KYCResubmitPage = lazyRetry(() => import('@/pages/KYC/routes/resubmit'));
const KYCVerifiedPage = lazyRetry(() => import('@/pages/KYC/routes/verified'));

// not-found
const NotFoundPage = lazyRetry(() => import('@/pages/NotFound'));

// Định nghĩa các routes
const routes: RouteObject[] = [
  {
    path: '/entity-selection',
    element: (
      <AuthWrapper>
        <Middleware mode="private">
          <WrapperRouteComponent title="Choose a business">
            <EntitySelectionScreen />
          </WrapperRouteComponent>
        </Middleware>
      </AuthWrapper>
    ),
  },
  {
    path: '/invite/id/:invitationId',
    element: (
      <WrapperRouteComponent title="Entity invitation">
        <AuthLayout />
      </WrapperRouteComponent>
    ),
    children: [
      {
        index: true,
        element: <InviteIdPage />,
      },
    ],
  },
  {
    path: '/',
    element: (
      <AuthWrapper>
        <Middleware mode="private">
          <EntityWorkspaceGate>
            <WrapperRouteComponent title="">
              <MainLayout />
            </WrapperRouteComponent>
          </EntityWorkspaceGate>
        </Middleware>
      </AuthWrapper>
    ),
    children: [
      {
        path: '/',
        element: <WrapperRouteComponent title="Dashboard"><ComingSoonPage /></WrapperRouteComponent>,
      },
      {
        // Legacy home: login redirects, NotFound and the KYC-verified CTA all
        // point at /dashboard — alias it until a real dashboard page exists.
        path: '/dashboard',
        element: <WrapperRouteComponent title="Dashboard"><ComingSoonPage /></WrapperRouteComponent>,
      },
      {
        path: '/notifications',
        element: (
          <WrapperRouteComponent title="Notifications">
            <NotificationsPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/coming-soon',
        element: <WrapperRouteComponent title="Coming soon"><ComingSoonPage /></WrapperRouteComponent>,
      },
      {
        path: '/wallets',
        element: <WrapperRouteComponent title="Wallets"><WalletsPage /></WrapperRouteComponent>,
      },
      {
        path: '/wallets/:id',
        element: <WrapperRouteComponent title="Wallet details"><SendMoneyModalProvider><WalletDetailPage /></SendMoneyModalProvider></WrapperRouteComponent>,
      },
      {
        path: '/wallets/:id/top-up',
        element: <WrapperRouteComponent title="Top up wallet"><WalletTopUpPage /></WrapperRouteComponent>,
      },
      {
        path: '/wallets/:id/convert',
        element: <WrapperRouteComponent title="Convert currencies"><WalletConvertPage /></WrapperRouteComponent>,
      },
      {
        path: '/wallets/:id/send-money',
        element: <WrapperRouteComponent title="Send money"><WalletSendMoneyPage /></WrapperRouteComponent>,
      },
      {
        path: '/fx',
        element: <WrapperRouteComponent title="FX Conversion"><FxPage /></WrapperRouteComponent>,
      },
      {
        path: '/wallets/:id/payout',
        element: <WrapperRouteComponent title="External transfer"><WalletPayoutPage /></WrapperRouteComponent>,
      },
      {
        path: '/receiving-accounts',
        element: <WrapperRouteComponent title="Receiving Accounts"><ReceivingAccountsPage /></WrapperRouteComponent>,
      },
      {
        path: '/cards',
        element: (
          <WrapperRouteComponent title="Cards">
            <WalletCardModalProvider>
              <DrawerProvider width={480}>
                <CardsPage />
              </DrawerProvider>
            </WalletCardModalProvider>
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/transactions',
        element: (
          <WrapperRouteComponent title="Transactions">
            <DrawerProvider width={480}>
              <TransactionsPage />
            </DrawerProvider>
          </WrapperRouteComponent>
        ),
      },
      { path: '/virtual-account', element: <Navigate to="/wallets" replace /> },
      { path: '/card', element: <Navigate to="/cards" replace /> },
      { path: '/transaction', element: <Navigate to="/transactions" replace /> },
      {
        path: '/settings/departments',
        element: <WrapperRouteComponent title="Departments"><DepartmentsPage /></WrapperRouteComponent>,
      },
      {
        path: '/settings/departments/:departmentId',
        element: <WrapperRouteComponent title="Department details"><DepartmentDetailPage /></WrapperRouteComponent>,
      },
      {
        path: '/settings/permissions',
        element: (
          <WrapperRouteComponent title="Entity permissions">
            <EntityPermissionsPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/settings/notifications',
        element: <WrapperRouteComponent title="Notification settings"><NotificationSettingsPage /></WrapperRouteComponent>,
      },
      {
        path: '/settings/permissions/:membershipId',
        element: (
          <WrapperRouteComponent title="Member permissions">
            <EntityPermissionUserDetailPage />
          </WrapperRouteComponent>
        ),
      },
      { path: '/settings', element: <Navigate to="/profile" replace /> },
      {
        path: '/profile',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.PROFILE}>
            <ProfilePage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '*',
        element: (
          <WrapperRouteComponent title="Coming soon">
            <ComingSoonPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    // Public: the page itself gates on auth — logged-out visitors get the token
    // parked and are routed to /register; RedirectHome resumes the acceptance.
    path: '/invite/:token',
    element: (
      <AuthWrapper>
        <WrapperRouteComponent title="Team invitation">
          <InviteAcceptPage />
        </WrapperRouteComponent>
      </AuthWrapper>
    ),
  },
  {
    path: '/',
    element: (
      <WrapperRouteComponent title="">
        <Middleware mode="non-login">
          <AuthLayout />
        </Middleware>
      </WrapperRouteComponent>
    ),
    children: [
      {
        path: 'login',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <AuthPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'register',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <AuthPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'verify-code',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.VERIFY_CODE}>
            <AuthPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.RESET_PASSWORD}>
            <AuthPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'verify',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <AuthPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'verify-email',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <AuthPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <AuthPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    path: '/kyc',
    element: (
      <AuthWrapper>
        <Middleware mode="private">
          <EntityOnboardingGate>
            <WrapperRouteComponent title="">
              <KYCLayout />
            </WrapperRouteComponent>
          </EntityOnboardingGate>
        </Middleware>
      </AuthWrapper>
    ),
    children: [
      {
        path: 'submit',
        element: (
          <WrapperRouteComponent title="KYC Submit">
            <KYCSubmitPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'pending',
        element: (
          <WrapperRouteComponent title="KYC Pending">
            <KYCPendingPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'resubmit',
        element: (
          <WrapperRouteComponent title="KYC Resubmit">
            <KYCResubmitPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'verified',
        element: (
          <WrapperRouteComponent title="KYC Verified">
            <KYCVerifiedPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    path: '/not-found',
    element: (
      <WrapperRouteComponent title={PAGE_TITLES.NOT_FOUND}>
        <NotFoundPage />
      </WrapperRouteComponent>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/not-found" replace />,
  },
];

// Export router
export const router = createBrowserRouter(routes);
